import { useSearchParams, Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { PageHeader } from "@/components/layout/AppShell";
import { Card } from "@/components/ui/Card";
import { SearchInput } from "@/components/ui/Input";
import { Pagination, PageInfo } from "@/components/ui/Pagination";
import { Badge } from "@/components/ui/Badge";
import { DateDisplay } from "@/components/shared/DateDisplay";
import { useRfqForms } from "@/hooks/useRfq";
import { formatTHB } from "@/lib/utils";
import type { RfqForm } from "@/lib/pocketbase";

export function RfqPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") || "";
  const page = parseInt(searchParams.get("page") || "1");

  const { data, isLoading } = useRfqForms({ search, page, perPage: 20 });

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set("q", value);
    } else {
      params.delete("q");
    }
    params.set("page", "1");
    setSearchParams(params);
  };

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", String(newPage));
    setSearchParams(params);
  };

  return (
    <div>
      <PageHeader
        title="บทสรุปผู้บริหาร"
        description="เรียกดูบทสรุปผู้บริหาร"
      />

      <div className="mb-6">
        <SearchInput
          placeholder="ค้นหาตามชื่อโครงการ..."
          defaultValue={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="max-w-md"
        />
      </div>

      {data && (
        <div className="mb-4">
          <PageInfo
            currentPage={data.page}
            pageSize={data.perPage}
            totalItems={data.totalItems}
          />
        </div>
      )}

      {isLoading && <LoadingState />}

      {data && data.items.length === 0 && (
        <Card className="p-8 text-center">
          <Icon
            icon="solar:checklist-line-duotone"
            className="w-12 h-12 text-slate-300 mx-auto mb-3"
          />
          <h3 className="font-semibold text-slate-900">ไม่พบบทสรุปผู้บริหาร</h3>
        </Card>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="flex flex-col gap-3">
            {data.items.map((form) => (
              <RfqFormCard key={form.id} form={form} />
            ))}
          </div>
          <div className="mt-6">
            <Pagination
              currentPage={data.page}
              totalPages={data.totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </div>
  );
}

function RfqFormCard({ form }: { form: RfqForm }) {
  const tender = form.expand?.tender_id as any;
  const projectName =
    tender?.project_name || form.project_name || "ไม่ทราบชื่อโครงการ";
  const egpProjectNo = tender?.egp_project_no || "-";
  const displayDate = form.request_date || form.submission_window_start;

  return (
    <Link to={`/tenders/${form.tender_id}?tab=rfq`}>
      <Card hover>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" size="sm">
                <Icon icon="solar:checklist-line-duotone" className="w-3 h-3" />
                บทสรุปผู้บริหาร
              </Badge>
              <span className="text-xs text-slate-500">เลขที่โครงการ</span>
              <span className="font-mono text-xs text-slate-700">
                {egpProjectNo}
              </span>
            </div>
            <p className="font-medium text-slate-900 mt-2 line-clamp-2">
              {projectName}
            </p>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
              {form.line_count && <span>{form.line_count} รายการ</span>}
              {displayDate ? (
                <DateDisplay date={displayDate} format="relative" />
              ) : (
                <span>-</span>
              )}
            </div>
          </div>
          {form.total_amount && (
            <div className="text-right">
              <p className="text-xs text-slate-500">รวม</p>
              <p className="font-medium text-slate-900">
                {formatTHB(form.total_amount)}
              </p>
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <Card key={i} className="animate-pulse">
          <div className="h-4 w-24 bg-slate-200 rounded" />
          <div className="h-4 w-48 bg-slate-200 rounded mt-2" />
          <div className="h-3 w-32 bg-slate-200 rounded mt-2" />
        </Card>
      ))}
    </div>
  );
}
